import React, { useState, useEffect, useMemo } from "react";
import { IonModal, IonIcon } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline, closeOutline } from "ionicons/icons";

interface YearPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (year: number) => void;
  /** Year – calendar opens showing this year */
  initialYear?: number;
  /** Year – earliest selectable year */
  minYear?: number;
  /** Year – latest selectable year */
  maxYear?: number;
}

const YearPicker: React.FC<YearPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialYear,
  minYear,
  maxYear,
}) => {
  const [viewDecade, setViewDecade] = useState(() => {
    const currentYear = new Date().getFullYear();
    return Math.floor(currentYear / 10) * 10;
  });
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialYear) {
        setViewDecade(Math.floor(initialYear / 10) * 10);
        setSelected(initialYear);
      } else {
        const currentYear = new Date().getFullYear();
        setViewDecade(Math.floor(currentYear / 10) * 10);
        setSelected(null);
      }
    }
  }, [isOpen, initialYear]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const years = useMemo(() => {
    const decadeYears = [];
    for (let i = 0; i < 12; i++) {
      decadeYears.push(viewDecade - 1 + i);
    }
    return decadeYears;
  }, [viewDecade]);

  const handleYearClick = (year: number) => {
    if (minYear && year < minYear) return;
    if (maxYear && year > maxYear) return;
    setSelected(year);
  };

  const handleGo = () => {
    if (selected !== null) {
      onSelect(selected);
    }
  };

  const prevDecade = () => setViewDecade(viewDecade - 10);
  const nextDecade = () => setViewDecade(viewDecade + 10);

  const formatDisplay = (year: number) => {
    return `Year ${year}`;
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="year-picker-modal"
      style={
        {
          "--width": "400px",
          "--max-width": "95vw",
          "--height": "auto",
          "--border-radius": "24px",
          "--backdrop-opacity": "0.5",
          "--box-shadow": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        } as React.CSSProperties
      }
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
        }}
      >
        {/* Header with Gradient */}
        <div
          style={{
            background: "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)",
            padding: "28px 28px 40px",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.35)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <IonIcon
              icon={closeOutline}
              style={{ color: "white", fontSize: "20px" }}
            />
          </button>

          {/* Title */}
          <div style={{ textAlign: "center" }}>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "white",
                margin: "0 0 8px",
                letterSpacing: "-0.5px",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              Go to Year
            </h2>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "14px",
                margin: 0,
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {selected !== null ? formatDisplay(selected) : "Select a year to jump to"}
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div
          style={{
            background: "#ffffff",
            marginTop: "-20px",
            borderRadius: "24px 24px 0 0",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Decade Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 24px 12px",
            }}
          >
            <button
              onClick={prevDecade}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronBackOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {viewDecade} - {viewDecade + 9}
            </span>
            <button
              onClick={nextDecade}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronForwardOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
          </div>

          {/* Year Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
              padding: "0 24px 16px",
            }}
          >
            {years.map((year) => {
              const isToday = year === currentYear;
              const isSelected = year === selected;
              const isOutsideDecade = year < viewDecade || year > viewDecade + 11;
              const disabled =
                isOutsideDecade ||
                (!!minYear && year < minYear) ||
                (!!maxYear && year > maxYear);

              return (
                <button
                  key={year}
                  onClick={() => !disabled && handleYearClick(year)}
                  disabled={disabled}
                  style={{
                    height: "48px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: isSelected ? 700 : isToday ? 700 : 400,
                    borderRadius: "12px",
                    border: "none",
                    cursor: disabled ? "default" : "pointer",
                    transition: "all 0.15s ease",
                    fontFamily: "Figtree, sans-serif",
                    color: disabled
                      ? "#d1d5db"
                      : isSelected
                      ? "#ffffff"
                      : isToday
                      ? "#05acd9"
                      : "#374151",
                    background: isSelected
                      ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                      : "transparent",
                    boxShadow: isSelected
                      ? "0 4px 12px rgba(5, 172, 217, 0.3)"
                      : "none",
                    opacity: isOutsideDecade ? 0.3 : 1,
                  }}
                >
                  {year}
                </button>
              );
            })}
          </div>

          {/* Go Button */}
          <div
            style={{
              padding: "16px 24px 24px",
              borderTop: "1px solid #f1f5f9",
              background:
                "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <button
              onClick={handleGo}
              disabled={selected === null}
              style={{
                width: "100%",
                height: "48px",
                borderRadius: "30px",
                border: "none",
                fontSize: "17px",
                fontWeight: 600,
                color: "white",
                cursor: selected !== null ? "pointer" : "default",
                fontFamily: "Figtree, sans-serif",
                letterSpacing: "-0.3px",
                transition: "all 0.2s ease",
                background: selected !== null
                  ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                  : "#cbd5e1",
                boxShadow: selected !== null
                  ? "0 4px 16px rgba(5, 172, 217, 0.3)"
                  : "none",
                opacity: selected !== null ? 1 : 0.6,
              }}
            >
              Go
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .year-picker-modal {
          --ion-backdrop-opacity: 0.5;
        }
      `}</style>
    </IonModal>
  );
};

export default YearPicker;
